import type { MarketListing, MarketProperty, MarketSubjectSnapshot, MarketTransaction } from "../domain/types.ts";
import { dedupePublicMarketListings } from "./crossSourceDedupe.ts";
import { marketListingToCandidate } from "./marketListingCandidate.ts";
import type { MarketCandidate, MarketCandidateOptions, MarketDataProvider } from "./marketDataProvider.ts";
import { normalizePublicMarketListing } from "./publicMarketNormalization.ts";
import {
  type PublicMarketDiscoveryReport,
  type PublicMarketQuery,
  type PublicMarketSourceAdapter,
  type PublicMarketSourceStatus
} from "./publicMarketTypes.ts";
import { normalizeRoomCount, roomSearchVariants } from "./roomNormalization.ts";

const unavailableStatus = (adapter: PublicMarketSourceAdapter, capturedAt: string): PublicMarketSourceStatus => ({
  providerId: adapter.id,
  sourceName: adapter.name,
  status: "unavailable",
  rawCount: 0,
  message: "Zdroj nemá nakonfigurovaný povolený feed alebo API.",
  capturedAt
});

export class MultiSourcePublicMarketProvider implements MarketDataProvider {
  readonly id = "public-market-multi-source";
  readonly available: boolean;
  private readonly adapters: PublicMarketSourceAdapter[];
  private readonly listings = new Map<string, MarketListing>();
  private report?: PublicMarketDiscoveryReport;

  constructor(adapters: PublicMarketSourceAdapter[]) {
    this.adapters = adapters;
    this.available = adapters.some((adapter) => adapter.available);
  }

  getDiscoveryReport() {
    return this.report;
  }

  async findCandidates(subject: MarketSubjectSnapshot, _options?: MarketCandidateOptions): Promise<MarketCandidate[]> {
    const capturedAt = new Date().toISOString();
    const rooms = normalizeRoomCount(subject.rooms);
    const query: PublicMarketQuery = {
      city: subject.city,
      municipalityId: subject.municipalityId,
      propertyType: subject.propertyType,
      rooms,
      roomVariants: roomSearchVariants(rooms),
      transactionType: subject.transactionType,
      area: subject.area,
      locality: subject.locality,
      street: subject.street,
      capturedAt
    };

    const settled = await Promise.all(this.adapters.map(async (adapter) => {
      if (!adapter.available) return { listings: [], status: unavailableStatus(adapter, capturedAt) };
      try {
        return await adapter.discover(query);
      } catch (error) {
        return {
          listings: [],
          status: {
            providerId: adapter.id,
            sourceName: adapter.name,
            status: "failed" as const,
            rawCount: 0,
            message: error instanceof Error ? error.message : "Zdroj zlyhal bez detailu.",
            capturedAt
          }
        };
      }
    }));

    const rawListings = settled.flatMap((result) => result.listings);
    const normalized = rawListings.flatMap((raw) => {
      const listing = normalizePublicMarketListing(raw, query);
      return listing ? [listing] : [];
    });
    const unique = dedupePublicMarketListings(normalized);
    this.listings.clear();
    unique.forEach((listing) => this.listings.set(listing.id, listing));
    this.report = {
      capturedAt,
      rawCount: settled.reduce((sum, result) => sum + result.status.rawCount, 0),
      normalizedCount: normalized.length,
      uniqueCount: unique.length,
      preliminaryCount: settled.reduce((sum, result) => sum + (result.status.preliminaryCount ?? result.listings.length), 0),
      detailCount: settled.reduce((sum, result) => sum + (result.status.detailCount ?? 0), 0),
      requestCount: settled.reduce((sum, result) => sum + (result.status.requestCount ?? 0), 0),
      sourceStatuses: settled.map((result) => result.status)
    };

    return unique.map((listing) => marketListingToCandidate(listing, subject, this.id));
  }

  async getListing(id: string) {
    return this.listings.get(id);
  }

  async getHistoricalListings() {
    return [];
  }

  async getRealizedTransactions(): Promise<MarketTransaction[]> {
    return [];
  }

  async getProperty(): Promise<MarketProperty | undefined> {
    return undefined;
  }
}

export const createUnavailablePublicMarketSource = (
  id: string,
  name: string,
  message: string
): PublicMarketSourceAdapter => ({
  id,
  name,
  available: false,
  async discover(query: PublicMarketQuery) {
    return {
      listings: [],
      status: {
        providerId: id,
        sourceName: name,
        status: "unavailable",
        rawCount: 0,
        message,
        capturedAt: query.capturedAt
      }
    };
  }
});
